import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatPriceLocal as formatPrice } from "../lib/api";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Lock, Mail, Building2, CreditCard, Landmark, Tag, X as XIcon, Check as CheckIcon, Truck } from "lucide-react";
import SEO from "../components/SEO";

export default function Checkout() {
  const { items, subtotal, clear, hydrated, hasPhysicalGaming } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null); // {code, discount, type, value}
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [shippingFee, setShippingFee] = useState(99);
  const [form, setForm] = useState({
    email: user?.email || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    company: user?.company || "",
    ic: user?.ic || "",
    dic: user?.dic || "",
    address: user?.address || "",
    city: user?.city || "",
    zip_code: user?.zip_code || "",
    country: "CZ",
    phone: user?.phone || "",
    // Delivery (only required if hasPhysicalGaming)
    delivery_same_as_billing: true,
    delivery_address: "",
    delivery_city: "",
    delivery_zip_code: "",
    delivery_country: "CZ",
    agree_terms: false,
    agree_digital_disclaimer: false,
    newsletter: false,
    payment_method: "stripe",
    notes: "",
  });

  useEffect(() => {
    api.get("/site-settings").then((r) => {
      if (typeof r.data?.gaming_shipping_fee === "number") setShippingFee(r.data.gaming_shipping_fee);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyVoucher = async () => {
    const code = voucherCode.trim().toUpperCase();
    if (!code) return;
    setVoucherLoading(true);
    try {
      const r = await api.post("/vouchers/validate", { code, subtotal });
      setVoucher(r.data);
      toast.success(`Slevový kód ${r.data.code} uplatněn`);
    } catch (err) {
      setVoucher(null);
      toast.error(err?.response?.data?.detail || "Neplatný kód");
    } finally {
      setVoucherLoading(false);
    }
  };

  const removeVoucher = () => { setVoucher(null); setVoucherCode(""); };

  const discount = voucher?.discount || 0;
  const shipping = hasPhysicalGaming ? shippingFee : 0;
  const totalAfter = Math.max(0, subtotal + shipping - discount);

  const allPhysicalGaming = items.length > 0 && items.every((i) => i.kind === "gaming" && i.product_type === "physical");

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Košík je prázdný");
    if (!form.agree_terms) return toast.error("Musíte souhlasit s obchodními podmínkami");
    if (!allPhysicalGaming && !form.agree_digital_disclaimer) return toast.error("Musíte souhlasit s digitálním doručením");
    if (hasPhysicalGaming) {
      // Determine delivery address to send
      const useBilling = form.delivery_same_as_billing;
      if (!useBilling && (!form.delivery_address || !form.delivery_city || !form.delivery_zip_code)) {
        return toast.error("Vyplňte prosím doručovací adresu");
      }
    }
    setLoading(true);
    try {
      const lang = (localStorage.getItem("ss_lang") || "cs").toLowerCase();
      const LANG_CCY = { cs: "CZK", sk: "EUR", en: "EUR", hu: "HUF" };
      const delivery = hasPhysicalGaming ? (form.delivery_same_as_billing ? {
        delivery_address: form.address,
        delivery_city: form.city,
        delivery_zip_code: form.zip_code,
        delivery_country: form.country,
      } : {
        delivery_address: form.delivery_address,
        delivery_city: form.delivery_city,
        delivery_zip_code: form.delivery_zip_code,
        delivery_country: form.delivery_country,
      }) : {};
      const payload = {
        ...form,
        ...delivery,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, kind: i.kind || "shop" })),
        origin_url: window.location.origin,
        voucher_code: voucher?.code || null,
        display_currency: LANG_CCY[lang] || "CZK",
      };
      const r = await api.post("/orders", payload);
      if (r.data.checkout_url) {
        clear();
        window.location.href = r.data.checkout_url;
      } else {
        clear();
        navigate(`/dekujeme?order=${r.data.order.order_number}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chyba při zpracování");
    } finally {
      setLoading(false);
    }
  };

  if (!hydrated) {
    return <div className="pt-10 pb-20 text-center text-zinc-500">Načítání…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="pt-10 pb-20 text-center max-w-md mx-auto px-4">
        <h1 className="font-display text-3xl font-bold mb-3">Košík je prázdný</h1>
        <Link to="/produkty" className="btn-primary inline-block px-6 py-3 rounded-full font-semibold mt-4">Procházet produkty</Link>
      </div>
    );
  }

  return (
    <div className="pt-8 pb-20" data-testid="checkout-page">
      <SEO title="Pokladna" description="Bezpečná pokladna – Stripe nebo bankovní převod." noindex />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl sm:text-4xl font-bold mb-8">Pokladna</h1>
        <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Section title="Kontaktní údaje" icon={Mail}>
              <Input label="E-mail *" type="email" value={form.email} onChange={(v) => set("email", v)} required testid="ck-email"/>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Jméno *" value={form.first_name} onChange={(v) => set("first_name", v)} required testid="ck-firstname"/>
                <Input label="Příjmení *" value={form.last_name} onChange={(v) => set("last_name", v)} required testid="ck-lastname"/>
              </div>
              <Input label="Telefon" value={form.phone} onChange={(v) => set("phone", v)} testid="ck-phone"/>
            </Section>

            <Section title="Fakturační adresa" icon={Building2}>
              <Input label="Společnost (nepovinné)" value={form.company} onChange={(v) => set("company", v)} testid="ck-company"/>
              <div className="grid grid-cols-2 gap-3">
                <Input label="IČO" value={form.ic} onChange={(v) => set("ic", v)} testid="ck-ic"/>
                <Input label="DIČ" value={form.dic} onChange={(v) => set("dic", v)} testid="ck-dic"/>
              </div>
              <Input label="Adresa *" value={form.address} onChange={(v) => set("address", v)} required testid="ck-address"/>
              <div className="grid grid-cols-3 gap-3">
                <Input label="Město *" value={form.city} onChange={(v) => set("city", v)} required testid="ck-city"/>
                <Input label="PSČ *" value={form.zip_code} onChange={(v) => set("zip_code", v)} required testid="ck-zip"/>
                <div>
                  <label className="text-xs font-semibold text-zinc-600">Země *</label>
                  <select value={form.country} onChange={(e) => set("country", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-testid="ck-country">
                    <option value="CZ">Česká republika</option>
                    <option value="SK">Slovensko</option>
                  </select>
                </div>
              </div>
            </Section>

            {hasPhysicalGaming && (
              <Section title="Doručení fyzického zboží" icon={Truck}>
                <div className="text-sm text-zinc-600 mb-3">
                  Váš košík obsahuje fyzické zboží (hry na disku, herní příslušenství). Doručení Českou poštou / Zásilkovnou za <strong>{formatPrice(shippingFee)}</strong>.
                </div>
                <Checkbox checked={form.delivery_same_as_billing} onChange={(v) => set("delivery_same_as_billing", v)} testid="ck-delivery-same">
                  Doručovací adresa je stejná jako fakturační
                </Checkbox>
                {!form.delivery_same_as_billing && (
                  <div className="mt-3 space-y-3" data-testid="ck-delivery-fields">
                    <Input label="Doručovací adresa *" value={form.delivery_address} onChange={(v) => set("delivery_address", v)} required testid="ck-del-address"/>
                    <div className="grid grid-cols-3 gap-3">
                      <Input label="Město *" value={form.delivery_city} onChange={(v) => set("delivery_city", v)} required testid="ck-del-city"/>
                      <Input label="PSČ *" value={form.delivery_zip_code} onChange={(v) => set("delivery_zip_code", v)} required testid="ck-del-zip"/>
                      <div>
                        <label className="text-xs font-semibold text-zinc-600">Země *</label>
                        <select value={form.delivery_country} onChange={(e) => set("delivery_country", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-testid="ck-del-country">
                          <option value="CZ">Česká republika</option>
                          <option value="SK">Slovensko</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </Section>
            )}

            <Section title="Způsob platby" icon={CreditCard}>
              <div className="space-y-2">
                <PayOption checked={form.payment_method === "stripe"} onChange={() => set("payment_method", "stripe")} icon={CreditCard} title="Karta, Apple Pay, Google Pay" desc="Bezpečná online platba přes Stripe. Klíč obdržíte ihned." testid="pay-stripe"/>
                <PayOption checked={form.payment_method === "bank_transfer"} onChange={() => set("payment_method", "bank_transfer")} icon={Landmark} title="Bankovní převod" desc="Obdržíte platební údaje. Klíč po připsání platby (1-2 prac. dny)." testid="pay-bank"/>
              </div>
            </Section>

            <Section title="Souhlasy">
              <Checkbox checked={form.agree_terms} onChange={(v) => set("agree_terms", v)} testid="agree-terms">
                Souhlasím s <Link to="/obchodni-podminky" className="text-blue-600">obchodními podmínkami</Link> a <Link to="/gdpr" className="text-blue-600">zpracováním osobních údajů</Link>.
              </Checkbox>
              {!allPhysicalGaming && (
                <Checkbox checked={form.agree_digital_disclaimer} onChange={(v) => set("agree_digital_disclaimer", v)} testid="agree-digital">
                  Souhlasím s okamžitým dodáním digitálního obsahu a beru na vědomí, že tímto ztrácím právo odstoupit od smlouvy v 14denní lhůtě.
                </Checkbox>
              )}
              <Checkbox checked={form.newsletter} onChange={(v) => set("newsletter", v)} testid="agree-newsletter">
                Chci odebírat novinky a slevové akce (volitelné).
              </Checkbox>
            </Section>
          </div>

          {/* Summary */}
          <div>
            <div className="sticky top-24 bg-white rounded-2xl border border-zinc-200 p-6">
              <div className="font-display font-bold text-xl mb-4">Souhrn</div>
              <div className="space-y-3 mb-4 border-b border-zinc-100 pb-4">
                {items.map((it) => (
                  <div key={it.product_id} className="flex justify-between text-sm">
                    <div className="text-zinc-700 line-clamp-1 pr-2">{it.name} × {it.quantity}</div>
                    <div className="font-semibold tabular-nums">{formatPrice(it.price * it.quantity)}</div>
                  </div>
                ))}
              </div>

              {/* Voucher */}
              <div className="mb-4">
                {!voucher ? (
                  <div>
                    <label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5"/>Slevový kód</label>
                    <div className="flex gap-2 mt-1.5">
                      <input
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                        placeholder="LAUNCH10"
                        className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 bg-white text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        data-testid="voucher-input"
                      />
                      <button
                        type="button"
                        onClick={applyVoucher}
                        disabled={voucherLoading || !voucherCode}
                        className="px-4 py-2 rounded-xl bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50"
                        data-testid="voucher-apply-btn"
                      >
                        {voucherLoading ? "…" : "Uplatnit"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5" data-testid="voucher-applied">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckIcon className="w-4 h-4 text-emerald-600"/>
                      <div>
                        <span className="font-semibold text-emerald-900">{voucher.code}</span>
                        <span className="text-emerald-700 ml-1.5">−{formatPrice(voucher.discount)}</span>
                      </div>
                    </div>
                    <button type="button" onClick={removeVoucher} className="text-zinc-400 hover:text-zinc-700" aria-label="Odebrat kód"><XIcon className="w-4 h-4"/></button>
                  </div>
                )}
              </div>

              {discount > 0 && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-600">Mezisoučet</span>
                  <span className="tabular-nums">{formatPrice(subtotal)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex justify-between text-sm mb-3 text-emerald-700">
                  <span>Sleva ({voucher.code})</span>
                  <span className="tabular-nums">−{formatPrice(discount)}</span>
                </div>
              )}
              {hasPhysicalGaming && (
                <div className="flex justify-between text-sm mb-3" data-testid="checkout-shipping-row">
                  <span className="text-zinc-600 inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5"/> Doručení na adresu</span>
                  <span className="tabular-nums">{formatPrice(shipping)}</span>
                </div>
              )}
              <div className="flex justify-between font-display font-bold text-2xl mb-5"><span>Celkem</span><span data-testid="checkout-total">{formatPrice(totalAfter)}</span></div>
              {(() => {
                try {
                  const lang = (localStorage.getItem("ss_lang") || "cs").toLowerCase();
                  if (lang === "cs") return null;
                  const fmtCzk = new Intl.NumberFormat("cs-CZ", { style: "currency", currency: "CZK", maximumFractionDigits: 0 }).format(totalAfter);
                  return (
                    <div className="text-xs text-zinc-500 mb-4 -mt-3 text-right" data-testid="checkout-czk-note">
                      = {fmtCzk}
                    </div>
                  );
                } catch (e) { return null; }
              })()}
              <button type="submit" disabled={loading} className="btn-primary w-full py-3.5 rounded-full font-semibold disabled:opacity-60" data-testid="checkout-submit-btn">
                {loading ? "Zpracovávám…" : <span className="flex items-center justify-center gap-2"><Lock className="w-4 h-4"/>Zaplatit {formatPrice(totalAfter)}</span>}
              </button>
              <div className="flex items-center gap-1 mt-4 text-xs text-zinc-500 justify-center"><Lock className="w-3 h-3"/>Šifrované, SSL zabezpečeno</div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-5 h-5 text-blue-600"/>}
        <div className="font-display font-bold text-lg">{title}</div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-600">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} type={type} required={required} data-testid={testid}
        className="w-full mt-1 px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
    </div>
  );
}

function Checkbox({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" data-testid={testid}/>
      <span>{children}</span>
    </label>
  );
}

function PayOption({ checked, onChange, icon: Icon, title, desc, testid }) {
  return (
    <label className={`flex gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${checked ? "border-blue-600 bg-blue-50/50" : "border-zinc-200 hover:border-zinc-300"}`} data-testid={testid}>
      <input type="radio" checked={checked} onChange={onChange} className="mt-1"/>
      <Icon className="w-5 h-5 text-zinc-700"/>
      <div className="flex-1">
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
